import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  BasicDialogService,
  DialogPresets,
} from '@principle-theorem/ng-shared';
import {
  MohPreApprovalFundingRequest,
  Patient,
  Practice,
  stafferToNamedDoc,
} from '@principle-theorem/principle-core';
import {
  type ITreatmentStep,
  FundingRequestStatus,
  IMohFormDisplay,
  IMohPreApprovalFundingRequest,
  IPatient,
  IPractice,
  IPricedServiceCodeEntry,
  IStaffer,
  MohFormDisplayLabel,
  MohGenerateFormRequest,
  MohGenerateFormRequestType,
  PatientRelationshipType,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  toNamedDocument,
  WithRef,
} from '@principle-theorem/shared';
import { combineLatest, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  MohConfirmPreApprovalDialogComponent,
  MohConfirmPreApprovalForm,
} from '../components/moh/moh-confirm-pre-approval-dialog/moh-confirm-pre-approval-dialog.component';
import {
  IMohSendPreApprovalDialogData,
  MohPreApprovalRequestForm,
  MohSendPreApprovalDialogComponent,
} from '../components/moh/moh-send-pre-approval-dialog/moh-send-pre-approval-dialog.component';
import { MohSignFormDialogComponent } from '../components/moh/moh-sign-form-dialog/moh-sign-form-dialog.component';
import { MohViewFormsDialogComponent } from '../components/moh/moh-view-forms-dialog/moh-view-forms-dialog.component';
import { MohPreApprovalPdfDataBuilder } from './pdf-data/moh-pre-approval-pdf-data-builder';

@Injectable()
export class MohPreApprovalService {
  private _dialog = inject(MatDialog);
  private _basicDialog = inject(BasicDialogService);
  private _snackBar = inject(MatSnackBar);

  getPreApprovalRequests$(
    practice$: Observable<WithRef<IPractice>>,
    patient$: Observable<WithRef<IPatient> | undefined>
  ): Observable<WithRef<IMohPreApprovalFundingRequest>[]> {
    return combineLatest([practice$, patient$]).pipe(
      switchMap(([practice, patient]) =>
        patient
          ? Patient.withPatientRelationships$(
              patient,
              [PatientRelationshipType.DuplicatePatient],
              (relatedPatient) =>
                MohPreApprovalFundingRequest.byPatient$(
                  practice,
                  relatedPatient
                )
            )
          : MohPreApprovalFundingRequest.all$(practice)
      )
    );
  }

  async generatePreApprovalRequest(
    step: WithRef<ITreatmentStep>,
    practice: WithRef<IPractice>,
    patient: WithRef<IPatient>,
    practitioner: WithRef<IStaffer>
  ): Promise<void> {
    const data: IMohSendPreApprovalDialogData = {
      step,
      practice,
      patient,
      practitioner,
    };
    await this._dialog
      .open(MohSendPreApprovalDialogComponent, DialogPresets.large({ data }))
      .afterClosed()
      .toPromise();
  }

  async markAsApproved(
    request: WithRef<IMohPreApprovalFundingRequest>
  ): Promise<void> {
    const formData = await this._dialog
      .open<
        MohConfirmPreApprovalDialogComponent,
        WithRef<IMohPreApprovalFundingRequest>,
        MohConfirmPreApprovalForm
      >(
        MohConfirmPreApprovalDialogComponent,
        DialogPresets.small({ data: request })
      )
      .afterClosed()
      .toPromise();
    if (!formData) {
      return;
    }

    await Firestore.patchDoc(request.ref, {
      data: {
        ...request.data,
        ...formData,
      },
      status: FundingRequestStatus.Approved,
    });
    this._snackBar.open('Request marked as Approved');
  }

  async markAsDeclined(
    request: WithRef<IMohPreApprovalFundingRequest>
  ): Promise<void> {
    await Firestore.patchDoc(request.ref, {
      status: FundingRequestStatus.Declined,
    });
    this._snackBar.open('Request marked as Declined');
  }

  async markAsCancelled(
    request: WithRef<IMohPreApprovalFundingRequest>
  ): Promise<void> {
    if (request.status === FundingRequestStatus.Submitted) {
      const confirmed = await this._basicDialog.confirm({
        title: 'Cancel Submitted Approval Request',
        prompt:
          'This approval request has been submitted to MOH. Are you sure you want to cancel?',
        submitColor: 'warn',
        submitLabel: 'Cancel',
      });
      if (!confirmed) {
        return;
      }
    }
    await Firestore.patchDoc(request.ref, {
      status: FundingRequestStatus.Cancelled,
    });
    this._snackBar.open('Request marked as Cancelled');
  }

  viewForm(request: WithRef<IMohPreApprovalFundingRequest>): void {
    if (!request.data.preApprovalFormPath) {
      return;
    }
    const form: IMohFormDisplay = {
      formPath: request.data.preApprovalFormPath,
      label: MohFormDisplayLabel.PreApprovalRequest,
    };
    this._dialog.open(
      MohViewFormsDialogComponent,
      DialogPresets.extraLarge({ data: [form] })
    );
  }

  async signAndSubmitApprovalRequest(
    request: WithRef<IMohPreApprovalFundingRequest>
  ): Promise<DocumentReference<IMohPreApprovalFundingRequest> | undefined> {
    const submittedRef = await this._dialog
      .open<
        MohSignFormDialogComponent,
        WithRef<IMohPreApprovalFundingRequest>,
        DocumentReference<IMohPreApprovalFundingRequest>
      >(
        MohSignFormDialogComponent,
        DialogPresets.almostFullscreen({ data: request })
      )
      .afterClosed()
      .toPromise();
    if (!submittedRef) {
      return;
    }

    this._snackBar.open('Approval Request Sent');
  }

  async buildPreApprovalRequest(
    dialogData: IMohSendPreApprovalDialogData,
    formData: MohPreApprovalRequestForm,
    codes: IPricedServiceCodeEntry[],
    isHighCaries: boolean
  ): Promise<MohGenerateFormRequest> {
    const preApprovalData = MohPreApprovalPdfDataBuilder.getPreApprovalData(
      formData,
      dialogData.step.ref,
      dialogData.practitioner,
      dialogData.practice,
      dialogData.patient,
      codes
    );
    const data = isHighCaries
      ? {
          ...preApprovalData,
          highCaries: await MohPreApprovalPdfDataBuilder.getHighCariesData(
            formData,
            dialogData.patient,
            dialogData.practitioner,
            dialogData.practice
          ),
        }
      : preApprovalData;

    const fundingRequest = MohPreApprovalFundingRequest.init({
      patient: toNamedDocument(dialogData.patient),
      practitioner: stafferToNamedDoc(dialogData.practitioner),
      data: {
        claimType: preApprovalData.claimType,
        stepRef: preApprovalData.stepRef,
      },
    });
    const requestRef = await Firestore.addDoc<IMohPreApprovalFundingRequest>(
      Practice.fundingRequestCol(dialogData.practice),
      fundingRequest
    );
    const type = isHighCaries
      ? MohGenerateFormRequestType.HighCaries
      : MohGenerateFormRequestType.PreApproval;

    return { type, requestRef, data } as MohGenerateFormRequest;
  }
}
